/**
 * The pick schedule editor's form logic (#102), shared by web
 * (`PickScheduleEditor.tsx`) and mobile (`NativePickScheduleEditor.tsx`) so
 * "Add requirement" and a changed start day build the same ranges on both
 * clients, and a bad range names the same field with the same words.
 *
 * This is form logic, not a contest rule: `validatePickSchedule` stays the
 * check, here and again on the server. It is reached through the
 * `@sportsmet/contest-engine/rules-draft` subpath, not the engine barrel.
 */
import type { PickRequirement } from "../rounds/pickSchedule";
import type { typeSurvivorRulesDraft } from "./survivorRulesDraft";

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function dayToUtc(day: string): Date | null {
  if (!DAY_PATTERN.test(day)) return null;
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** The calendar day `days` after (or before) `day`, or null for a bad day. */
export function shiftPickScheduleDay(day: string, days: number): string | null {
  const date = dayToUtc(day);
  if (date === null) return null;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function daysBetween(fromDay: string, toDay: string): number | null {
  const from = dayToUtc(fromDay);
  const to = dayToUtc(toDay);
  if (from === null || to === null) return null;
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

/**
 * "Sep 7, 2026" for "2026-09-07". Built by hand rather than through Intl, so
 * the words do not move with the runtime's ICU data, and a calendar day is
 * never shifted by a timezone.
 */
export function formatPickScheduleDay(day: string): string | null {
  const date = dayToUtc(day);
  if (date === null) return null;
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

export type typePickRangeFieldErrors = {
  startsOn?: string;
  endsOn?: string;
  timeZone?: string;
  requiredPicks?: string;
  revivedRequiredPicks?: string;
};

const WHOLE_PICKS_ERROR = "Enter a whole number from 1 to 1,000.";

/** The sentence under the ranges while any of them is invalid. */
export const PICK_SCHEDULE_ERROR_SUMMARY =
  "Check the schedule: each range needs valid dates, timezone and counts. Ranges cannot overlap, only the last can be open-ended, and the first cannot inherit a previous range.";

/**
 * validatePickSchedule's errors for one range, worded for the field they
 * belong to. Errors with no field of their own stay in the summary.
 */
export function pickRangeFieldErrors(input: {
  errors: readonly string[];
  index: number;
  item: PickRequirement;
}): typePickRangeFieldErrors {
  const prefix = `pickSchedule.requirements.${input.index}.`;
  const kinds = new Set(
    input.errors
      .filter((error) => error.startsWith(prefix))
      .map((error) => error.slice(prefix.length)),
  );
  const out: typePickRangeFieldErrors = {};
  if (kinds.has("dates")) {
    if (!DAY_PATTERN.test(input.item.startsOn))
      out.startsOn = "Choose the day this range starts.";
    else out.endsOn = "Choose a valid end day.";
  }
  if (kinds.has("startsOn"))
    out.startsOn = "This range starts before the contest does.";
  if (kinds.has("overlap"))
    out.startsOn = "This range overlaps the one before it. Start it later.";
  if (kinds.has("endsOn"))
    out.endsOn =
      input.item.endsOn === null
        ? "Only the last range can stay open. Choose an end day."
        : "End on or after the start day, and no later than the contest end.";
  if (kinds.has("timeZone")) out.timeZone = "Choose a timezone.";
  if (kinds.has("requiredPicks")) out.requiredPicks = WHOLE_PICKS_ERROR;
  if (kinds.has("revivedRequiredPicks"))
    out.revivedRequiredPicks = WHOLE_PICKS_ERROR;
  return out;
}

/**
 * Where "Add requirement" puts the new range. A range with known days is
 * split in half, so both halves are valid at once. A range with no known end
 * (a TBD contest) or a single day cannot be split: the new range starts blank
 * for the fan to choose, and choosing it closes the range before
 * (`setPickRangeStart`).
 */
export function splitForNewRange(input: {
  previous: PickRequirement | undefined;
  contestStartDate: string;
  contestEndDate: string | null;
}): { previousEndsOn: string | null; startsOn: string; endsOn: string | null } {
  const { previous } = input;
  if (!previous)
    return {
      previousEndsOn: null,
      startsOn: input.contestStartDate,
      endsOn: input.contestEndDate,
    };
  const lastDay = previous.endsOn ?? input.contestEndDate;
  const span =
    lastDay === null ? null : daysBetween(previous.startsOn, lastDay);
  if (lastDay !== null && span !== null && span >= 1) {
    const middle = shiftPickScheduleDay(
      previous.startsOn,
      Math.floor((span - 1) / 2),
    );
    const startsOn = middle === null ? null : shiftPickScheduleDay(middle, 1);
    if (middle !== null && startsOn !== null)
      return { previousEndsOn: middle, startsOn, endsOn: previous.endsOn };
  }
  return { previousEndsOn: previous.endsOn, startsOn: "", endsOn: null };
}

/** The contest's end day, or null while it is TBD. */
function contestEndDate(rules: typeSurvivorRulesDraft): string | null {
  return rules.endDateMode === "date" ? rules.endDate : null;
}

/**
 * The ranges after "Add requirement": the last range gives up the back half
 * of its days (see `splitForNewRange`) and the new range takes the contest's
 * current picking rules as its starting point.
 */
export function addPickRange(input: {
  rules: typeSurvivorRulesDraft;
  requirementId: string;
  /** The zone a first range starts in; later ranges copy the one before. */
  defaultTimeZone: string;
}): PickRequirement[] {
  const { rules } = input;
  const requirements = rules.pickSchedule?.requirements ?? [];
  const previous = requirements.at(-1);
  const split = splitForNewRange({
    previous,
    contestStartDate: rules.startDate,
    contestEndDate: contestEndDate(rules),
  });
  const next: PickRequirement = {
    requirementId: input.requirementId,
    startsOn: split.startsOn,
    endsOn: split.endsOn,
    timeZone: previous?.timeZone ?? input.defaultTimeZone,
    cadence: rules.roundGrouping,
    weekStartDay: rules.weekStartDay,
    requiredPicks: rules.requiredPicksPerRound ?? 1,
    revivedRequiredPicks: null,
    lockTiming: rules.roundLockTiming,
    teamReuseAllowed: rules.teamReuseAllowed,
    inheritPreviousReuse: false,
    extendPreviousGames: false,
    postponementPolicy: rules.postponementPolicy,
  };
  const kept = requirements.map((item, i) =>
    i === requirements.length - 1
      ? { ...item, endsOn: split.previousEndsOn }
      : item,
  );
  return [...kept, next];
}

/**
 * The ranges after range `index` gets a new start day. A later range that
 * gets its start closes an open range before it on the day before, so two
 * open-ended ranges never stand side by side.
 */
export function setPickRangeStart(
  requirements: readonly PickRequirement[],
  index: number,
  startsOn: string,
): PickRequirement[] {
  const before = requirements[index - 1];
  const dayBefore = shiftPickScheduleDay(startsOn, -1);
  return requirements.map((item, i) => {
    if (i === index) return { ...item, startsOn };
    if (
      i === index - 1 &&
      before?.endsOn === null &&
      dayBefore !== null &&
      dayBefore >= before.startsOn
    )
      return { ...item, endsOn: dayBefore };
    return item;
  });
}

/**
 * One line for a range's collapsed row: "Sep 7, 2026 to contest end · 1 pick
 * per week". A range whose start is not chosen yet says so.
 */
export function pickRangeSummary(item: PickRequirement): string {
  const start = formatPickScheduleDay(item.startsOn);
  const end =
    item.endsOn === null ? "contest end" : formatPickScheduleDay(item.endsOn);
  const dates =
    start === null
      ? "Start day not chosen"
      : end === null
        ? `From ${start}, end day not chosen`
        : `${start} to ${end}`;
  const period = item.cadence === "day" ? "game day" : "week";
  const count =
    Number.isInteger(item.requiredPicks) && item.requiredPicks > 0
      ? `${item.requiredPicks} ${item.requiredPicks === 1 ? "pick" : "picks"} per ${period}`
      : "Picks per round not set";
  return `${dates} · ${count}`;
}
